import React, {ReactNode} from 'react';

export interface AnnotationLike {
  startIndex: number;
  endIndex: number;
  clientSideUid: string;
}

interface AnnotationComponentProps<T extends AnnotationLike> {
  children: ReactNode;
  annotation: T;
}

export type AnnotationComponent<T extends AnnotationLike> = React.ComponentType<AnnotationComponentProps<T>>;
/*
 * Renders a string with annotations rendered at the right indices.
 *
 * @param text The text to render.
 * @param annotations The annotations to render in the text.
 * @param AnnotationComponent The function to render an annotation. It MUST render the provided children.
 * @param currentTextContentOffset The current text content offset when we begin parsing the processing the text (if any).
 * @returns The rendered node.
 */
export function renderStringWithAnnotations<T extends AnnotationLike>(
  text: string,
  annotations: ReadonlyArray<T>,
  AnnotationComponent: AnnotationComponent<T>,
  currentTextContentOffsetState: {index: number} = {index: 0},
): ReactNode {
  let currentIndexInText = 0;
  const openedAnnotationsIds = new Set<string>();
  const currentTextContentOffset = currentTextContentOffsetState.index;

  const filteredAnnotations = annotations.filter((annotation) => {
    // invalid annotation indices
    if (annotation.startIndex >= annotation.endIndex) {
      return false;
    }

    // begins after the text
    if (annotation.startIndex >= currentTextContentOffset + text.length) {
      return false;
    }
    // ends before the text
    if (annotation.endIndex <= currentTextContentOffset) {
      return false;
    }
    return true;
  });

  const findEligibleStartAnnotationAtIndex = (index: number) =>
    filteredAnnotations.find(
      (annotation) =>
        Math.max(annotation.startIndex, currentTextContentOffset) === index &&
        !openedAnnotationsIds.has(annotation.clientSideUid),
    );

  const findEligibleEndAnnotationAtIndex = (index: number) =>
    filteredAnnotations.find(
      (annotation) =>
        Math.min(annotation.endIndex, currentTextContentOffset + text.length) === index &&
        openedAnnotationsIds.has(annotation.clientSideUid),
    );

  const renderRecursively = (): ReactNode => {
    let textBuffer = '';
    let startAnnotation: T | undefined;
    let endAnnotation: T | undefined;

    while (currentIndexInText < text.length) {
      const offset = currentTextContentOffset + currentIndexInText;
      startAnnotation = findEligibleStartAnnotationAtIndex(offset);
      endAnnotation = findEligibleEndAnnotationAtIndex(offset);

      if (!startAnnotation && !endAnnotation) {
        textBuffer += text.charAt(currentIndexInText);
        currentIndexInText++;
      } else {
        // Exit the loop, there is an annotation to process
        break;
      }
    }

    if (endAnnotation) {
      openedAnnotationsIds.delete(endAnnotation.clientSideUid);
      return textBuffer;
    }
    if (startAnnotation) {
      openedAnnotationsIds.add(startAnnotation.clientSideUid);
      return (
        <>
          {textBuffer}
          <AnnotationComponent annotation={startAnnotation}>{renderRecursively()}</AnnotationComponent>
          {renderRecursively()}
        </>
      );
    }

    return textBuffer;
  };

  return renderRecursively();
}
